import React, { useState, useEffect } from "react";
import { 
  HiOutlineDocumentText, 
  HiOutlinePlus, 
  HiOutlineTrash, 
  HiOutlineSave,
  HiOutlineUserGroup,
  HiOutlineCalendar,
  HiOutlineCurrencyDollar,
  HiOutlineClipboardList,
  HiOutlineArrowLeft
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

export default function ProformaOlusturPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [formData, setFormData] = useState({
    proforma_no: '',
    partner_id: '',
    request_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    notes: '',
    currency: 'TRY',
    items: []
  });

  // Partner ve ürünleri yükle
  useEffect(() => {
    fetch('/api/partners')
      .then(res => res.json())
      .then(data => setPartners(data))
      .catch(() => setPartners([]));

    fetch('/api/products')
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(() => setProducts([]));
  }, []);

  // Proforma numarası oluştur
  useEffect(() => {
    if (!formData.proforma_no) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setFormData(prev => ({
        ...prev,
        proforma_no: `PRO-${year}${month}${day}-${random}`
      }));
    }
  }, [formData.proforma_no]);

  // Partner seçildiğinde
  const handlePartnerChange = (partnerId) => {
    const partner = partners.find(p => p.id == partnerId);
    setSelectedPartner(partner);
    setFormData(prev => ({ ...prev, partner_id: partnerId }));
  };

  // Ürün ekle
  const addItem = () => {
    const newItem = {
      id: Date.now(),
      product_id: '',
      product_name: '',
      product_code: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      promotion_quantity: 0,
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Ürün güncelle
  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Ürün seçildiğinde bilgileri doldur
    if (field === 'product_id') {
      const product = products.find(p => p.id == value);
      if (product) {
        updatedItems[index] = {
          ...updatedItems[index],
          product_name: product.name,
          product_code: product.code,
          unit_price: product.price || 0
        };
      }
    }

    // Toplam fiyat hesapla
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = field === 'quantity' ? value : updatedItems[index].quantity;
      const unitPrice = field === 'unit_price' ? value : updatedItems[index].unit_price;
      updatedItems[index].total_price = quantity * unitPrice;
    }

    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  // Ürün sil
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Proforma kaydet
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partner_id) {
      alert('Lütfen partner seçin');
      return;
    }

    if (formData.items.length === 0) {
      alert('Lütfen en az bir ürün ekleyin');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/proformas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Proforma başarıyla oluşturuldu');
        navigate('/proforma');
      } else {
        const error = await response.json();
        alert('Hata: ' + (error.message || 'Proforma oluşturulamadı'));
      }
    } catch (error) {
      alert('Hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Toplam tutar hesapla
  const totalAmount = formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);

  return (
    <div className="w-full p-6">
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/proforma')}
          className="flex items-center gap-2 px-3 py-2 text-blue-700 hover:bg-blue-50 rounded-lg transition"
        >
          <HiOutlineArrowLeft className="w-5 h-5" />
          Geri Dön
        </button>
        <h1 className="text-3xl font-bold text-blue-900 flex items-center gap-3">
          <HiOutlineDocumentText className="w-8 h-8 text-blue-700" />
          Yeni Proforma Oluştur
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-blue-100">
        {/* Temel Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Proforma No</label>
            <input
              type="text"
              value={formData.proforma_no}
              onChange={(e) => setFormData(prev => ({ ...prev, proforma_no: e.target.value }))}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900 font-mono"
              readOnly
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Partner</label>
            <select
              value={formData.partner_id}
              onChange={(e) => handlePartnerChange(e.target.value)}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              required
            >
              <option value="">Partner Seçin</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.partner_name} ({partner.partner_code})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Para Birimi</label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
            >
              <option value="TRY">TRY (₺)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Talep Tarihi</label>
            <input
              type="date"
              value={formData.request_date}
              onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value }))}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Teslimat Tarihi</label>
            <input
              type="date"
              value={formData.delivery_date}
              onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-2">Notlar</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              rows="1"
              placeholder="Proforma notları..."
            />
          </div>
        </div>

        {/* Seçili Partner Bilgileri */}
        {selectedPartner && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <HiOutlineUserGroup className="w-5 h-5" />
              Partner Bilgileri
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><b>Partner Adı:</b> {selectedPartner.partner_name}</div>
              <div><b>Partner Kodu:</b> {selectedPartner.partner_code}</div>
              <div><b>Şehir:</b> {selectedPartner.city}</div>
              <div><b>Ülke:</b> {selectedPartner.country}</div>
              <div><b>Adres:</b> {selectedPartner.address}</div>
              <div><b>Telefon:</b> {selectedPartner.phone}</div>
            </div>
          </div>
        )}

        {/* Ürün Listesi */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <HiOutlineClipboardList className="w-5 h-5" />
              Proforma Ürünleri
            </h3>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Ürün Ekle
            </button>
          </div>

          {formData.items.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Henüz ürün eklenmemiş. Ürün eklemek için "Ürün Ekle" butonuna tıklayın.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-blue-100">
              <table className="min-w-full text-sm">
                <thead className="bg-blue-50">
                  <tr className="text-blue-900">
                    <th className="px-3 py-2 text-left">Ürün</th>
                    <th className="px-3 py-2 text-left">Miktar</th>
                    <th className="px-3 py-2 text-left">Birim Fiyat</th>
                    <th className="px-3 py-2 text-left">Promosyon</th>
                    <th className="px-3 py-2 text-left">Toplam</th>
                    <th className="px-3 py-2 text-left">Notlar</th>
                    <th className="px-3 py-2 text-left">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={item.id} className="hover:bg-blue-50 transition">
                      <td className="px-3 py-2">
                        <select
                          value={item.product_id}
                          onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                          className="w-full px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-200 text-blue-900"
                          required
                        >
                          <option value="">Ürün Seçin</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.code})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-200 text-blue-900"
                          min="1"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-24 px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-200 text-blue-900"
                          min="0"
                          step="0.01"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.promotion_quantity}
                          onChange={(e) => updateItem(index, 'promotion_quantity', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-200 text-blue-900"
                          min="0"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-3 py-2 font-semibold">
                        {item.total_price?.toLocaleString('tr-TR')} {formData.currency}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.notes}
                          onChange={(e) => updateItem(index, 'notes', e.target.value)}
                          className="w-32 px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-200 text-blue-900"
                          placeholder="Not..."
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Toplam */}
        <div className="flex justify-end mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <HiOutlineCurrencyDollar className="w-5 h-5" />
              Toplam Tutar: {totalAmount.toLocaleString('tr-TR')} {formData.currency}
            </div>
          </div>
        </div>

        {/* Kaydet Butonu */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/proforma')}
            className="px-6 py-3 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            <HiOutlineSave className="w-4 h-4" />
            {loading ? 'Kaydediliyor...' : 'Proforma Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
}
